import { useRef, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, createPaginationContainer } from 'react-relay';
import {
    trackCarouselEvent,
    trackSellerPageClick,
    trackSellerItemClick,
    trackCarouselItemImpression,
} from '../../tracking/similarItemsTracking';

// components
import { Carousel } from 'dibs-carousel';
import { FormattedMessage } from 'dibs-react-intl';
import { SeoButtonComponent } from 'dibs-seo/exports/SeoButton';
import { PdpRespSimilarItem } from '../PdpRespSimilarItem/PdpRespSimilarItem';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { QuickViewProvider } from '../../../global/QuickViewProvider/QuickViewProvider';

// styles
import styles from './PdpRespSimilarItemTileCarousel.scss';

const ITEMS_PER_PAGE = 5;
const ITEMS_REFETCH_LIMIT = 9;

export function PdpRespSimilarItemTileCarouselComponent({ item, user, relay, viewer, currency }) {
    const { sellerItems } = viewer || {};
    const { serviceId: userId } = user || {};
    const { serviceId: itemPk, seller } = item || {};
    const { serviceId: sellerPk } = seller || {};
    const hasSellerItems = sellerItems?.edges?.length;
    const carouselRef = useRef(null);
    const impressionTrackingRef = useRef({});
    const [carouselPage, setCarouselPage] = useState(0);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [isCarouselVisible, setIsCarouselVisible] = useState(false);
    const [hasFetchedAllImages, setHasFetchedAllImages] = useState(false);

    const renderItem = useCallback(
        ({ index, quickView }) => {
            const similarItemEdge = sellerItems?.edges?.[index];
            const similarItemNode = similarItemEdge?.node;

            if (!similarItemNode) {
                return null;
            }

            return (
                <PdpRespSimilarItem
                    isMobile // setting to isMobile for quickview styles
                    user={user}
                    index={index}
                    viewer={viewer}
                    item={similarItemNode}
                    currency={currency}
                    quickView={quickView}
                    showMeasurements={false}
                    onClick={() => trackSellerItemClick(index, similarItemEdge)}
                />
            );
        },
        [sellerItems, currency, viewer, user]
    );

    const onArrowClick = useCallback(
        direction => {
            if (!hasFetchedAllImages) {
                setHasFetchedAllImages(true);
                relay.loadMore(ITEMS_REFETCH_LIMIT);
            }
            trackCarouselEvent(direction);
        },
        [hasFetchedAllImages, relay]
    );

    useEffect(() => {
        // if user logs in after paginating pdp refetch container resets this pagination container
        // in this case reset carousel
        if (userId) {
            setCarouselIndex(0);
            setHasFetchedAllImages(false);
        }
    }, [userId]);

    useEffect(() => {
        if (!isCarouselVisible) {
            return;
        }

        const startIndex = carouselPage * ITEMS_PER_PAGE;
        const visibleItems =
            sellerItems?.edges?.slice(startIndex, startIndex + ITEMS_PER_PAGE) || [];

        visibleItems.forEach(({ node }, index) => {
            const { ecommerceTrackingParams, categoryCode, categoryPath } = node || {};
            const { id } = ecommerceTrackingParams || {};

            if (!impressionTrackingRef.current[id]) {
                trackCarouselItemImpression({
                    ...node.ecommerceTrackingParams,
                    category: categoryPath,
                    categoryCode,
                    position: Math.floor(startIndex + index),
                });
                impressionTrackingRef.current[id] = true;
            }
        });
    }, [carouselPage, sellerItems, isCarouselVisible]);

    if (!hasSellerItems) {
        return null;
    }

    return (
        <div className={styles.container} ref={carouselRef} data-tn="pdp-dealer-grid">
            <span className={styles.title}>
                <FormattedMessage
                    defaultMessage="More From This Seller"
                    id="PdpRespSimilarItemTileCarousel.title"
                />
            </span>
            <VisibilityTracker
                elementRef={carouselRef}
                onVisibilityChange={({ isVisible }) => setIsCarouselVisible(isVisible)}
            />
            <QuickViewProvider>
                {({ quickView }) => (
                    <Carousel
                        isInfinite
                        slideCarouselItems
                        step={ITEMS_PER_PAGE}
                        index={carouselIndex}
                        itemsPerPage={ITEMS_PER_PAGE}
                        totalItems={sellerItems?.edges?.length}
                        onLeftArrowClick={() => onArrowClick('left')}
                        onRightArrowClick={() => onArrowClick('right')}
                        onPageChange={({ page }) => setCarouselPage(page)}
                        onIndexChange={({ index }) => setCarouselIndex(index)}
                        renderItem={({ index }) => renderItem({ index, quickView })}
                    />
                )}
            </QuickViewProvider>
            <div className={styles.viewAll}>
                <SeoButtonComponent
                    size="small"
                    type="secondary"
                    onClick={trackSellerPageClick}
                    linkData={{
                        path: `/more-from-seller/?itemPk=${itemPk}&sellerPk=${sellerPk}`,
                        isLinkable: false,
                    }}
                    dataTn="view-more-from-seller-button"
                >
                    <FormattedMessage
                        defaultMessage="View All From Seller"
                        id="PdpRespSimilarItemTileCarousel.viewAll"
                    />
                </SeoButtonComponent>
            </div>
        </div>
    );
}

PdpRespSimilarItemTileCarouselComponent.propTypes = {
    user: PropTypes.object,
    currency: PropTypes.string,
    item: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
};

export const PdpRespSimilarItemTileCarousel = createPaginationContainer(
    PdpRespSimilarItemTileCarouselComponent,
    {
        viewer: graphql`
            fragment PdpRespSimilarItemTileCarousel_viewer on Viewer
            @argumentDefinitions(
                after: { type: "String" }
                first: { type: "Int!", defaultValue: 6 }
            ) {
                ...PdpRespSimilarItem_viewer
                sellerItems: moreLikeThisItem(
                    first: $first
                    after: $after
                    seller: INCLUDE
                    itemId: $itemId
                ) @connection(key: "PdpRespSimilarItemTileCarousel_sellerItems", filters: []) {
                    edges {
                        node {
                            ecommerceTrackingParams
                            categoryPath
                            categoryCode
                            serviceId
                            browseUrl
                            title
                            ...PdpRespSimilarItem_item
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpRespSimilarItemTileCarousel_user on User {
                serviceId
                ...PdpRespSimilarItem_user
            }
        `,
        item: graphql`
            fragment PdpRespSimilarItemTileCarousel_item on Item {
                serviceId
                seller {
                    serviceId
                }
            }
        `,
    },
    {
        direction: 'forward',
        getConnectionFromProps(props) {
            return props.viewer && props.viewer.sellerItems;
        },
        getFragmentVariables(prevVars, first) {
            return {
                ...prevVars,
                first,
            };
        },
        getVariables(props, { count, cursor }, fragmentVariables) {
            return {
                ...fragmentVariables,
                first: count,
                after: cursor,
            };
        },
        query: graphql`
            query PdpRespSimilarItemTileCarouselRefetchQuery(
                $itemId: String!
                $isTrade: Boolean!
                $after: String
                $first: Int!
            ) {
                viewer {
                    ...PdpRespSimilarItemTileCarousel_viewer
                    @arguments(first: $first, after: $after)
                }
            }
        `,
    }
);
